import { qs, qsa, remove, add } from '@fluorescent/dom';

import { section } from '@/glow';
import { on, emit } from '@/glow/events';
import navigationDots from '@/lib/navigation-dots';

const selectors = {
  slider: '[data-slider]',
  slide: '.slideshow__cell',
};

section('slideshow', {
  events: [],
  slideshow: null,
  dotNavigation: null,

  onLoad() {
    const slider = qs(selectors.slider, this.container);
    const slides = qsa(selectors.slide, this.container);
    const { autoplay } = slider.dataset;
    import('animejs/lib/anime.es.js'); // load this before we need it

    remove(slider, 'is-hidden');
    // trigger redraw for transition
    slider.offsetHeight;

    // Animate the first slide only when it is scrolled into view
    const observer = new IntersectionObserver(
      ([{ intersectionRatio: visible }]) => {
        if (visible) {
          this._animateSlide(slides[0]);
          this._unpause();
          observer.disconnect();
        }
      }
    );

    if (slides.length > 1) {
      import('flickity').then(({ default: Flickity }) => {
        this.slideshow = new Flickity(slider, {
          adaptiveHeight: true,
          autoPlay: Number(autoplay),
          draggable: true,
          prevNextButtons: false,
          wrapAround: true,
          pageDots: false,
          dragThreshold: 5,
          pauseAutoPlayOnHover: !window.Shopify.designMode,
          on: {
            ready: () => {
              observer.observe(this.container);
            },
          },
        });

        // Pause player from playing while offscreen allowing text animation
        // when player intersects
        this._pause();

        this.dotNavigation = navigationDots(this.container, this.slideshow);

        this.slideshow.on('change', index => {
          const activeSlide = this.slideshow.cells[index].element;
          this._animateSlide(activeSlide);
          this.dotNavigation && this.dotNavigation.update(index);
        });

        emit('slideshow:initialized');
      });
    } else if (slides.length) {
      add(slides[0], 'is-selected');
      this._animateSlide(slides[0]);
    }
  },

  _pause() {
    this.slideshow && this.slideshow.pausePlayer();
  },

  _unpause() {
    this.slideshow && this.slideshow.unpausePlayer();
  },

  _handleBlockSelect(slideIndex) {
    this.slideshow.select(slideIndex);
    this._pause();
  },

  _animateSlide(target) {
    import('animejs/lib/anime.es.js').then(({ default: anime }) => {
      anime({
        targets: qsa('.animate', target),
        easing: 'easeOutQuart',
        translateY: [
          { value: 40, duration: 0 },
          { value: 0, duration: 500 },
        ],
        opacity: [
          { value: 0, duration: 0 },
          { value: 1, duration: 500 },
        ],
        delay: anime.stagger(150, { start: 800 }),
      });
    });
  },

  onBlockSelect({ target }) {
    if (this.slideshow) {
      this._handleBlockSelect(target.dataset.index);
    } else {
      // Listen for initalization if slideshow does not exist
      this.events.push(
        on('slideshow:initialized', () => {
          this._handleBlockSelect(target.dataset.index);
        })
      );
    }
  },

  onBlockDeselect() {
    if (this.slideshow) {
      this._unpause();
    } else {
      // Listen for initalization if slideshow does not exist
      this.events.push(
        on('slideshow:initialized', () => {
          this._unpause();
        })
      );
    }
  },

  onUnload() {
    this.slideshow && this.slideshow.destroy();
    this.events.forEach(unsubscribe => unsubscribe());
    this.dotNavigation && this.dotNavigation.unload();
  },
});
